import api from "../../utils/api";
import { normalizeOffer } from "../../client/dashboard/customOffersApi";

export { formatOfferPrice, formatOfferDate, OFFER_STATUS } from "../../client/dashboard/customOffersApi";

const asArray = (value) => (Array.isArray(value) ? value : []);

function normalizeCustomerRow(raw) {
  return {
    id: raw.id,
    fullName: raw.full_name || "",
    email: raw.email || "",
    companyName: raw.company_name || "",
    accountStatus: raw.account_status || "trial",
    dateJoined: raw.date_joined || null,
    currentOffer: raw.current_offer
      ? {
          id: raw.current_offer.id,
          status: raw.current_offer.status,
          reference: raw.current_offer.reference,
          title: raw.current_offer.title,
          price: raw.current_offer.price,
          billingInterval: raw.current_offer.billing_interval,
        }
      : null,
  };
}

export async function fetchAdminOffers({ status, search, userId, page } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  if (userId) params.set("user", String(userId));
  if (page) params.set("page", String(page));
  const qs = params.toString();
  const data = await api.get(`/api/admin/offers/${qs ? `?${qs}` : ""}`, { auth: true });
  return {
    count: data?.count ?? 0,
    results: asArray(data?.results).map(normalizeOffer),
  };
}

export async function fetchAdminOfferCustomers({ search, hasOffer, userId, page } = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (hasOffer === true) params.set("has_offer", "yes");
  if (hasOffer === false) params.set("has_offer", "no");
  if (userId) params.set("user_id", String(userId));
  if (page) params.set("page", String(page));
  const qs = params.toString();
  const data = await api.get(`/api/admin/offers/customers/${qs ? `?${qs}` : ""}`, { auth: true });
  return {
    count: data?.count ?? 0,
    results: asArray(data?.results).map(normalizeCustomerRow),
  };
}

export async function fetchAdminOfferOverview() {
  const data = await api.get("/api/admin/offers/overview/", { auth: true });
  return {
    totalOffers: data?.total_offers ?? 0,
    byStatus: data?.by_status || {},
    customersTotal: data?.customers_total ?? 0,
    customersWithOffer: data?.customers_with_offer ?? 0,
    customersWithoutOffer: data?.customers_without_offer ?? 0,
  };
}

/** Editor form → API payload. */
export function toOfferPayload(form) {
  return {
    ...(form.userId ? { user_id: Number(form.userId) } : {}),
    title: form.title.trim(),
    description: form.description || "",
    price: String(form.price).replace(",", "."),
    billing_interval: form.billingInterval,
    features: form.features
      .filter((f) => f.label.trim())
      .map((f) => ({ label: f.label.trim(), included: f.included !== false })),
    limits: form.limits
      .filter((l) => l.label.trim())
      .map((l) => ({ label: l.label.trim(), value: String(l.value ?? "").trim() })),
    valid_until: form.validUntil ? new Date(form.validUntil).toISOString() : null,
    admin_notes: form.adminNotes || "",
    ...(form.activate ? { activate: true } : {}),
  };
}

export async function createAdminOffer(form) {
  const data = await api.post("/api/admin/offers/", toOfferPayload(form), { auth: true });
  return normalizeOffer(data);
}

export async function updateAdminOffer(offerId, form) {
  const payload = toOfferPayload(form);
  delete payload.user_id;
  delete payload.activate;
  const data = await api.patch(`/api/admin/offers/${offerId}/`, payload, { auth: true });
  return normalizeOffer(data);
}

export async function activateAdminOffer(offerId) {
  const data = await api.post(`/api/admin/offers/${offerId}/activate/`, {}, { auth: true });
  return normalizeOffer(data);
}

export async function deactivateAdminOffer(offerId) {
  const data = await api.post(`/api/admin/offers/${offerId}/deactivate/`, {}, { auth: true });
  return normalizeOffer(data);
}

export async function deleteAdminOffer(offerId) {
  return api.delete(`/api/admin/offers/${offerId}/`, { auth: true });
}
